import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAuth } from '@contexts/useAuth';
import { selectSiteTz } from '@store/selectors/site.selectors';

/**
 * Time-of-day greeting in JST (matches the rest of the product's
 * timezone discipline). The hour is computed in the PROJECT's zone rather
 * than from `new Date().getHours()`, so the greeting matches the working day
 * of the site being reviewed rather than the reviewer's own clock — and stays
 * stable for anyone opening the page from another zone.
 */
export function useGreeting(): string {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz);
  const siteHour = tz
    ? Number(
        new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          hour: '2-digit',
          hour12: false,
        }).format(new Date()),
      )
    : null;
  if (siteHour === null) return t('aiAgent.greeting.afternoon');
  if (siteHour < 12) return t('aiAgent.greeting.morning');
  if (siteHour < 17) return t('aiAgent.greeting.afternoon');
  return t('aiAgent.greeting.evening');
}

/**
 * The greeting addressed to the reader — "Good morning, kenji!".
 *
 * The name is the signed-in username, the one name the session carries. Without
 * one the greeting still reads, rather than inventing a placeholder.
 */
export function useNamedGreeting(): string {
  const { t } = useTranslation();
  const greeting = useGreeting();
  const name = useAuth().user?.username;
  return name
    ? t('aiAgent.greeting.named', { greeting, name })
    : t('aiAgent.greeting.unnamed', { greeting });
}
